import React from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { Redirect, Route, RouteProps } from 'react-router';
import { message } from 'antd';
import { LoadableComponent } from '@loadable/component';
import type { AppState } from 'shared/store/reducers';
import log from 'shared/utils/log';
import { selectActiveUser, setAuthForwardLocation } from 'shared/store/auth-slice';
import Loader from 'shared/components/Loader';
import { SocketKickModal } from 'shared/components/SocketKickModal';
import {
  selectActivePublicUser,
  selectAuthedAccount,
} from 'shared/store/shared-selectors';
import { NoUserMessage } from './NoUserMessage';

interface OwnProps {
  loggedInOrPublic?: boolean;
  onlyLoggedOut?: boolean;
}

const mapState = (state: AppState) => ({
  account: selectAuthedAccount(state),
  user: selectActiveUser(state),
  publicUser: selectActivePublicUser(state),
  hasCheckedUser: state.auth.hasCheckedUser,
  authForwardLocation: state.auth.authForwardLocation,
});

const mapDispatch = {
  setAuthForwardLocation,
};

const connector = connect(mapState, mapDispatch);

type Props = OwnProps & RouteProps & ConnectedProps<typeof connector>;

class AuthRoute extends React.Component<Props> {
  componentDidMount() {
    this.setAuthForward();
    if (!this.props.hasCheckedUser) {
      this.preloadComponent();
    }
  }

  private preloadComponent() {
    const { component } = this.props as { component?: LoadableComponent<{}> };
    if (component && 'preload' in component) {
      log.debug(
        `Optimistically preloading AuthRoute component at path ${this.props.location?.pathname}`,
      );
      component.preload();
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { hasCheckedUser } = this.props;
    // in case we mounted before having checked user
    if (!prevProps.hasCheckedUser && hasCheckedUser) {
      this.setAuthForward();
    }
  }

  public render() {
    const {
      account,
      user,
      publicUser,
      loggedInOrPublic,
      onlyLoggedOut,
      hasCheckedUser,
      location,
      authForwardLocation,
      ...routeProps
    } = this.props;

    if (!hasCheckedUser) {
      return <Loader size="large" />;
    }

    // Display a special message for accounts with no active users.
    if (account && !user) {
      if (location?.pathname !== '/') {
        return <RedirectToRoot />;
      }
      return <NoUserMessage />;
    }

    if ((user && !onlyLoggedOut) || (!user && onlyLoggedOut) || loggedInOrPublic) {
      return (
        <>
          {(!!user || !!publicUser) && <SocketKickModal />}
          <Route {...routeProps} />
        </>
      );
    } else {
      const isRepOnboarding = location?.pathname?.startsWith('/rep-onboarding');

      let newLocation = {
        ...location,
        pathname: isRepOnboarding ? '/rep-onboarding/auth' : '/auth',
      };

      if (onlyLoggedOut) {
        newLocation = authForwardLocation || {
          ...location,
          pathname: isRepOnboarding ? '/rep-onboarding' : '/',
        };
      }

      return (
        <>
          <noscript className="noScript is-block">
            This page requires you to login before you can access it, but you have
            Javascript disabled. Please enable Javascript and login to continue.
          </noscript>
          <Redirect to={{ ...newLocation }} />
        </>
      );
    }
  }

  private setAuthForward() {
    const {
      setAuthForwardLocation,
      location,
      hasCheckedUser,
      account,
      loggedInOrPublic,
      onlyLoggedOut,
      authForwardLocation,
    } = this.props;
    if (hasCheckedUser && !account && !onlyLoggedOut && !loggedInOrPublic) {
      message.destroy('auth-required');
      message.warn({ content: 'Authorization required.', key: 'auth-required' });
      setAuthForwardLocation(authForwardLocation || location);
    }
  }
}

const RedirectToRoot = () => (
  <>
    <noscript className="noScript is-block">
      This page requires you to complete setup before you can access it, but you have
      Javascript disabled. Please enable Javascript and complete setup to continue.
    </noscript>
    <Redirect to="/" />
  </>
);

export default connector(AuthRoute);
